import { Highlight } from 'components/Images/Highlights/HighlightTypes'
import { isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import React, { useMemo } from 'react'
import {
  PdfFeature,
  PdfPage,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppSelector } from 'store/configureStore'
import { CircleHighlight } from './Highlights/CircleHighlight'
import { LineHeightLight } from './Highlights/LineHighlight'
import { PolygonHighlight } from './Highlights/PolygonHighlight'
import { RectangleHightlight } from './Highlights/RectangleHightlight'
import { SVGHighlight } from './Highlights/SVGHighlight'

type Props = {
  bomItemPointer: BomItemPointer
  highlights: {
    [key: string]: {
      highlight: Highlight
      pdfPage: PdfPage
      pdfFeature: PdfFeature
    }
  }
  divRef: React.RefObject<HTMLDivElement>
  scale: number
  position: { x: number; y: number }
  imgWidth: number
  imgHeight: number
  rotation: string
  paddingTopFix: number
}

const EngDrawingsHighlights = (props: Props) => {
  const currentCheckList = useAppSelector(
    (state) => state.project.currentCheckList,
    isEqual,
  )

  const checkListContainsFeatureId = React.useCallback(
    (featureId: string) => {
      if (!currentCheckList) {
        return false
      }

      const hasFeature = Object.values(currentCheckList.checkList || {})
        .flatMap((x) => x.map((y) => y.featureId))
        .find((x) => x === featureId)?.[0]

      return Boolean(hasFeature)
    },
    [currentCheckList],
  )

  const featureIsActive = React.useCallback(
    (featureId: string) => {
      if (!currentCheckList) {
        return false
      }

      return currentCheckList.activeFeatureId === featureId
    },
    [currentCheckList],
  )

  const featureIsDone = React.useCallback(
    (featureId: string) => {
      if (!currentCheckList) {
        return false
      }

      // may have two or more items with the same featureId
      const checkListItems = Object.values(currentCheckList.checkList || {})
        .flatMap((x) => x)
        .filter((x) => x.featureId === featureId)

      if (!checkListItems?.length) {
        return false
      }

      const notDoneItems = checkListItems.filter((x) => !x.done)

      if (notDoneItems.length > 0) {
        return false
      }

      return true
    },
    [currentCheckList],
  )

  const highlightComponents = useMemo(() => {
    return Object.values(props.highlights || {}).map((highlight) => {
      const isCheckListFeature = checkListContainsFeatureId(
        highlight.pdfFeature.id,
      )
      const isFeatureActive = featureIsActive(highlight.highlight.id)
      const isFeatureDone = featureIsDone(highlight.highlight.id)

      switch (highlight.highlight.type) {
        case 'circle':
          return (
            <CircleHighlight
              key={highlight.highlight.id}
              {...highlight.highlight}
              {...props}
              divRef={props.divRef}
              pdfPage={highlight.highlight.pdfPage}
              pdfFeature={highlight.highlight.pdfFeature}
              isCheckListFeature={isCheckListFeature}
              isActiveCheckListFeature={isFeatureActive}
              isDoneCheckListFeature={isFeatureDone}
            />
          )
        case 'rectangle':
          return (
            <RectangleHightlight
              key={highlight.highlight.id}
              {...highlight.highlight}
              {...props}
              divRef={props.divRef}
              pdfPage={highlight.highlight.pdfPage}
              pdfFeature={highlight.highlight.pdfFeature}
              isCheckListFeature={isCheckListFeature}
              isActiveCheckListFeature={isFeatureActive}
              isDoneCheckListFeature={isFeatureDone}
            />
          )
        case 'polygon':
          return (
            <PolygonHighlight
              key={highlight.highlight.id}
              {...highlight.highlight}
              scale={props.scale}
              position={props.position}
              paddingTopFix={props.paddingTopFix}
              rotation={props.rotation}
              imgHeight={props.imgHeight}
              imgWidth={props.imgWidth}
              divRef={props.divRef}
              pdfPage={highlight.highlight.pdfPage}
              pdfFeature={highlight.highlight.pdfFeature}
              isCheckListFeature={isCheckListFeature}
              isActiveCheckListFeature={isFeatureActive}
              isDoneCheckListFeature={isFeatureDone}
              bomItemPointer={props.bomItemPointer}
            />
          )
        case 'line':
          return (
            <LineHeightLight
              key={highlight.highlight.id}
              {...highlight.highlight}
              {...props}
              divRef={props.divRef}
              pdfPage={highlight.highlight.pdfPage}
              pdfFeature={highlight.highlight.pdfFeature}
              isCheckListFeature={isCheckListFeature}
              isActiveCheckListFeature={isFeatureActive}
              isDoneCheckListFeature={isFeatureDone}
            />
          )
      }
    })
  }, [checkListContainsFeatureId, featureIsActive, featureIsDone, props])

  return <SVGHighlight {...props}>{highlightComponents}</SVGHighlight>
}

export default React.memo(EngDrawingsHighlights, isEqual)
